import { useEffect, useId, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { Dialog } from '../../components/Dialog.js';
import { ApiError } from '../../api/client.js';
import type {
  CreateMetricRequest,
  MetricEntry,
  UpdateMetricRequest,
} from '../../api/metricsTypes.js';
import { isFutureIso, todayIso } from './bmiUtils.js';

export interface MetricEntryDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial: MetricEntry | null;
  onClose: () => void;
  onCreate: (req: CreateMetricRequest) => Promise<MetricEntry>;
  onUpdate: (
    id: number,
    req: UpdateMetricRequest,
  ) => Promise<MetricEntry>;
}

interface FieldErrors {
  measured_at?: string;
  weight_kg?: string;
  body_fat_pct?: string;
}

const inputStyle = (hasError: boolean): React.CSSProperties => ({
  background: 'var(--bg-primary)',
  borderColor: hasError ? '#dc2626' : 'var(--border)',
  color: 'var(--text-main)',
});

export function MetricEntryDialog({
  open,
  mode,
  initial,
  onClose,
  onCreate,
  onUpdate,
}: MetricEntryDialogProps): ReactElement {
  const dateId = useId();
  const weightId = useId();
  const bfId = useId();
  const notesId = useId();

  const [measuredAt, setMeasuredAt] = useState<string>(todayIso());
  const [weight, setWeight] = useState<string>('');
  const [bodyFat, setBodyFat] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSubmitError(null);
    if (mode === 'edit' && initial) {
      setMeasuredAt(initial.measured_at);
      setWeight(String(initial.weight_kg));
      setBodyFat(
        initial.body_fat_pct !== null ? String(initial.body_fat_pct) : '',
      );
      setNotes(initial.notes ?? '');
    } else {
      setMeasuredAt(todayIso());
      setWeight('');
      setBodyFat('');
      setNotes('');
    }
  }, [open, mode, initial]);

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (mode === 'create') {
      if (!measuredAt) {
        next.measured_at = 'Date is required';
      } else if (isFutureIso(measuredAt)) {
        next.measured_at = 'Date cannot be in the future';
      }
    }
    const w = Number(weight);
    if (weight.trim() === '' || !Number.isFinite(w) || w <= 0 || w > 500) {
      next.weight_kg = 'Weight must be between 0.1 and 500 kg';
    }
    if (bodyFat.trim() !== '') {
      const bf = Number(bodyFat);
      if (!Number.isFinite(bf) || bf <= 0 || bf > 60) {
        next.body_fat_pct = 'Body fat must be between 0 and 60';
      }
    }
    return next;
  }

  async function onSubmit(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const weightKg = Number(weight);
      const bodyFatPct =
        bodyFat.trim() === '' ? null : Number(bodyFat);
      const notesValue = notes.trim() === '' ? null : notes.trim();
      if (mode === 'edit' && initial) {
        const req: UpdateMetricRequest = {
          weight_kg: weightKg,
          body_fat_pct: bodyFatPct,
          notes: notesValue,
        };
        await onUpdate(initial.id, req);
      } else {
        const req: CreateMetricRequest = {
          measured_at: measuredAt,
          weight_kg: weightKg,
          body_fat_pct: bodyFatPct,
          notes: notesValue,
        };
        await onCreate(req);
      }
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to save';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const title = mode === 'edit' ? 'Edit measurement' : 'New measurement';
  const submitLabel = mode === 'edit' ? 'Save' : 'Save';
  const submittingLabel = mode === 'edit' ? 'Saving…' : 'Saving…';

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-3"
        data-testid="metric-entry-form"
      >
        <div className="flex flex-col gap-1 text-sm">
          <label
            htmlFor={dateId}
            style={{ color: 'var(--text-muted)' }}
          >
            Date
          </label>
          {mode === 'edit' ? (
            <span
              data-testid="metric-entry-date-display"
              style={{ color: 'var(--text-main)' }}
            >
              {measuredAt}
            </span>
          ) : (
            <>
              <input
                id={dateId}
                type="date"
                value={measuredAt}
                onChange={(e) => setMeasuredAt(e.target.value)}
                className="rounded-md border px-3 py-2 text-base outline-none"
                style={inputStyle(Boolean(errors.measured_at))}
                data-testid="metric-entry-date"
                required
              />
              {errors.measured_at ? (
                <span
                  className="text-xs"
                  style={{ color: '#dc2626' }}
                  data-testid="metric-entry-error-date"
                >
                  {errors.measured_at}
                </span>
              ) : null}
            </>
          )}
        </div>

        <div className="flex flex-col gap-1 text-sm">
          <label
            htmlFor={weightId}
            style={{ color: 'var(--text-muted)' }}
          >
            Weight (kg)
          </label>
          <input
            id={weightId}
            type="number"
            inputMode="decimal"
            min={0.1}
            max={500}
            step={0.1}
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="rounded-md border px-3 py-2 text-base outline-none"
            style={inputStyle(Boolean(errors.weight_kg))}
            data-testid="metric-entry-weight"
            required
          />
          {errors.weight_kg ? (
            <span
              className="text-xs"
              style={{ color: '#dc2626' }}
              data-testid="metric-entry-error-weight"
            >
              {errors.weight_kg}
            </span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1 text-sm">
          <label
            htmlFor={bfId}
            style={{ color: 'var(--text-muted)' }}
          >
            Body fat (%) — optional
          </label>
          <input
            id={bfId}
            type="number"
            inputMode="decimal"
            min={0}
            max={60}
            step={0.1}
            value={bodyFat}
            onChange={(e) => setBodyFat(e.target.value)}
            className="rounded-md border px-3 py-2 text-base outline-none"
            style={inputStyle(Boolean(errors.body_fat_pct))}
            data-testid="metric-entry-bf"
          />
          {errors.body_fat_pct ? (
            <span
              className="text-xs"
              style={{ color: '#dc2626' }}
              data-testid="metric-entry-error-bf"
            >
              {errors.body_fat_pct}
            </span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1 text-sm">
          <label
            htmlFor={notesId}
            style={{ color: 'var(--text-muted)' }}
          >
            Notes — optional
          </label>
          <input
            id={notesId}
            type="text"
            maxLength={200}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="rounded-md border px-3 py-2 text-base outline-none"
            style={inputStyle(false)}
            data-testid="metric-entry-notes"
          />
        </div>

        {submitError ? (
          <div
            role="alert"
            className="rounded-md border p-2 text-sm"
            style={{
              borderColor: '#dc2626',
              color: '#dc2626',
              background: 'color-mix(in srgb, #dc2626 8%, transparent)',
            }}
            data-testid="metric-entry-submit-error"
          >
            {submitError}
          </div>
        ) : null}

        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border px-4 py-2 text-sm"
            style={{
              background: 'transparent',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
              minHeight: 48,
            }}
            data-testid="metric-entry-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md px-4 py-2 text-sm font-medium"
            style={{
              background: 'var(--accent)',
              color: 'var(--accent-contrast)',
              minHeight: 48,
              opacity: submitting ? 0.7 : 1,
            }}
            data-testid="metric-entry-submit"
          >
            {submitting ? submittingLabel : submitLabel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
